/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ThermalRegistrationDataInfo from "../../ThermographicInspectionRecord/ThermalRegistrationDataInfo";
import ThermogramSection from "pages/thermography/ThermographicInspectionRecord/ThermogramSection";
import ThermogramMetaDataTable from "pages/thermography/ThermographicInspectionRecord/ThermogramMetaDataTable";
import ThermogramReferenceImage from "pages/thermography/ThermographicInspectionRecord/ThermogramReferenceImage";
import ThermalAnalysisDataInfo from "pages/thermography/ThermographicInspectionRecord/ThermalAnalysisDataInfo";
import SignatureBlock from "./SignatureBlock";

import { IThermogram, createDefaultThermogram } from "pages/thermography/model/thermogram.model";
import { AnomalyRecord, anomalyRecordDefaultValue } from "data/entities/AnomalyRecord";
import { DatabaseService } from "pages/thermography/stores/DatabaseService";

import ReportTemplateBase, { ReportBlock } from "../Templates/ReportTemplateBase";
import logo from "assets/img/icons/logoTech.png";
import { ConditionTypeLabel } from "pages/thermography/model/enumerations/condition-type.model";
import { getDateLocaleConfig } from "i18n/locale";
import { toUploadUrl } from "helpers/utils";

import "./ThermographicInspectionRecordReport.css";

export interface ThermographicInspectionRecordReportProps {
    /** opcional: se não vier, pega da URL (:codigo) */
    codigo?: string;
    anomalyRecord?: any;
    /** opcionais (pra embutir no relatório sem precisar ter tudo no pai) */
    powerStations?: any[];
    equipmentsFiltered?: any[];
    inspectionRoutesFiltered?: any[];
    thermographers?: any[];
    equipmentType?: string;

    /** assinatura controlada (quando o pai quer gerenciar por código) */
    thermographerId?: string;
    managerId?: string;
    onThermographerChange?: (v: string) => void;
    onManagerChange?: (v: string) => void;

    pageNumberStart?: number;
    totalPages?: number;
    onPagesCount?: (count: number) => void
}

export default function ThermographicInspectionRecordReport({
    codigo: codigoProp,
    anomalyRecord: anomalyRecordProp,
    powerStations = [],
    equipmentsFiltered = [],
    inspectionRoutesFiltered = [],
    thermographers = [],
    equipmentType: equipmentTypeProp = "",

    thermographerId: thermographerIdProp,
    managerId: managerIdProp,
    onThermographerChange,
    onManagerChange,
    pageNumberStart,
    totalPages,
    onPagesCount
}: ThermographicInspectionRecordReportProps) {
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });

    const { codigo: codigoFromUrl } = useParams<{ codigo: string }>();
    const codigo = String(codigoProp ?? codigoFromUrl ?? "").trim();

    const dbService = DatabaseService.getInstance();
    const anomalyRecords = dbService.getAnomalyRecords();

    const emissionDateRef = React.useRef<Date>(new Date());
    const emissionDateFormatted = useMemo(() => {
        return new Intl.DateTimeFormat(getDateLocaleConfig(i18n.language).intlLocale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        }).format(emissionDateRef.current);
    }, [i18n.language]);


    const anomalyRecord: AnomalyRecord = useMemo(() => {
        if (anomalyRecordProp) return anomalyRecordProp as any;

        if (!codigo) return anomalyRecordDefaultValue as any;

        return (anomalyRecords.find((x: any) => String(x?.id) === String(codigo)) || anomalyRecordDefaultValue) as any;
    }, [anomalyRecordProp, anomalyRecords, codigo]);

    const thermogram: IThermogram = useMemo(() => {
        return (anomalyRecord?.thermogram ?? createDefaultThermogram()) as any;
    }, [anomalyRecord]);

    const thermogramRef: IThermogram = useMemo(() => {
        return (anomalyRecord?.thermogramRef ?? createDefaultThermogram()) as any;
    }, [anomalyRecord]);

    const baseUrl = useMemo(() => process.env.REACT_APP_BACKEND_URL || "/", []);

    const termogramUrl = useMemo(() => {
        const p = (thermogram?.imagePath ?? "").trim();
        return p ? toUploadUrl(p, baseUrl) : null;
    }, [baseUrl, thermogram]);

    const termogramRefUrl = useMemo(() => {
        const p = (thermogramRef?.imagePath ?? "").trim();
        return p ? toUploadUrl(p, baseUrl) : null;
    }, [baseUrl, thermogramRef]);

    const imageUrlRef = useMemo(() => {
        const p = (thermogramRef?.imageRefPath ?? "").trim();
        return p ? toUploadUrl(p, baseUrl) : null;
    }, [baseUrl, thermogramRef]);

    const deltaT = useMemo(() => {
        const d = (thermogram?.maxTempRoi ?? 0) - (thermogramRef?.maxTempRoi ?? 0);
        return parseFloat((d || 0).toFixed(1));
    }, [thermogram, thermogramRef]);

    // ==========================
    // assinatura (controlada ou local)
    // ==========================
    const [localThermographerId, setLocalThermographerId] = useState("");
    const [localManagerId, setLocalManagerId] = useState("");

    const thermographerId = thermographerIdProp ?? localThermographerId;
    const managerId = managerIdProp ?? localManagerId;

    const setThermographer = onThermographerChange ?? setLocalThermographerId;
    const setManager = onManagerChange ?? setLocalManagerId;

    // ==========================
    // deps auxiliares
    // ==========================
    const equipmentComponentLimites = useMemo(() => {
        return dbService
            .getEquipmentComponents()
            .toSorted((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
    }, [dbService]);

    const riskPeriodDeadlineList = useMemo(() => {
        return dbService.getRiskPeriodicityDeadline();
    }, [dbService]);

    const equipmentType = useMemo(() => {
        if (equipmentTypeProp && String(equipmentTypeProp).trim()) return equipmentTypeProp;

        return (
            anomalyRecord?.equipment?.name ??
            "--"
        );
    }, [equipmentTypeProp, anomalyRecord]);

    const conditionLabel = useMemo(() => {
        const c = (anomalyRecord as any)?.condition;
        return (ConditionTypeLabel as any)?.[c] ?? "--";
    }, [anomalyRecord]);

    // ==========================
    // blocks
    // ==========================
    const blocks: ReportBlock[] = useMemo(
        () => [
            {
                key: `registration-${codigo || "no-code"}`,
                node: (
                    <div className="thermo-report__registration-data">
                        <ThermalRegistrationDataInfo
                            mode="report"
                            anomalyRecord={anomalyRecord}
                            powerStations={powerStations}
                            equipmentsFiltered={equipmentsFiltered}
                            inspectionRoutesFiltered={inspectionRoutesFiltered}
                            thermographers={thermographers}
                            equipmentType={equipmentType}
                        />
                    </div>
                ),
            },
            {
                key: `thermograms-${codigo || "no-code"}`,
                node: (
                    <div className="thermo-report__thermograms-grid">
                        <div className="thermo-report__cell">
                            <ThermogramSection
                                title={tt("pages.thermography.thermalAnomalyImages.monitoringThermogram")}
                                mode="view"
                                thermogram={thermogram}
                                termogramUrl={termogramUrl}
                            />
                        </div>

                        <div className="thermo-report__cell">
                            <ThermogramSection
                                title={tt("pages.thermography.thermalAnomalyImages.referenceThermogram")}
                                mode="view"
                                thermogram={thermogramRef}
                                termogramUrl={termogramRefUrl}
                            />
                        </div>

                        <div className="thermo-report__cell">
                            <ThermogramMetaDataTable thermogram={thermogram} tempRef={thermogramRef.maxTempRoi} deltaT={deltaT} />
                        </div>

                        <div className="thermo-report__cell thermo-report__cell--refimg">
                            <ThermogramReferenceImage mode="view" imageUrlRef={imageUrlRef} />
                        </div>
                    </div>
                ),
            },
            {
                key: `analysis-summary-${codigo || "no-code"}`,
                node: (
                    <div className="thermo-report__analysis thermo-report__analysis--summary">
                        <ThermalAnalysisDataInfo
                            mode="report"
                            anomalyRecord={anomalyRecord}
                            equipmentComponentLimites={equipmentComponentLimites}
                            riskPeriodDeadlineList={riskPeriodDeadlineList}
                        />
                    </div>
                ),
            },
            {
                key: `analysis-body-${codigo || "no-code"}`,
                node: (
                    <div className="thermo-report__analysis thermo-report__analysis--body">
                        <ThermalAnalysisDataInfo
                            mode="report"
                            anomalyRecord={anomalyRecord}
                            equipmentComponentLimites={equipmentComponentLimites}
                            riskPeriodDeadlineList={riskPeriodDeadlineList}
                        />
                    </div>
                ),
            },
            {
                key: `signatures-${codigo || "no-code"}`,
                node: (
                    <SignatureBlock
                        peopleOptions={thermographers}
                        thermographerId={thermographerId}
                        managerId={managerId}
                        onThermographerChange={setThermographer}
                        onManagerChange={setManager}
                    />
                ),
            },
        ],
        [
            codigo,
            anomalyRecord,
            powerStations,
            equipmentsFiltered,
            inspectionRoutesFiltered,
            thermographers,
            equipmentType,
            thermogram,
            thermogramRef,
            termogramUrl,
            termogramRefUrl,
            imageUrlRef,
            deltaT,
            equipmentComponentLimites,
            riskPeriodDeadlineList,
            thermographerId,
            managerId,
            setThermographer,
            setManager,
            t,
        ]
    );

    return (
        <ReportTemplateBase
            className="thermo-report"
            title={tt("pages.thermography.thermographicInspectionRecordReport.title")}
            logoSrc={logo}
            logoWidth={170}
            headerInfo={{
                subtitle: "",
                company: {
                    name: "TECH Monitoramento da Condição LTDA.",
                    cnpj: "63.153.215/0001-62",
                    address: "Rua Dezesseis de Março 415, Sala 805, Centro, Petrópolis - RJ",

                },
                docBox: [
                    { label: tt("pages.thermography.thermographicInspectionRecordReport.docBox.emission"), value: emissionDateFormatted },
                    { label: tt("pages.thermography.thermographicInspectionRecordReport.docBox.equipment"), value: equipmentType },
                    { label: tt("pages.thermography.thermographicInspectionRecordReport.docBox.condition"), value: conditionLabel },
                ],
            }}
            blocks={blocks}
            pageNumberStart={pageNumberStart}
            totalPages={totalPages}
            onPagesCount={onPagesCount}
        />
    );
}
